import React, { useState } from 'react';
import { Element } from 'react-scroll';
import { motion, AnimatePresence } from 'framer-motion';
import { fadeInUp, staggerContainer, cardVariant } from '../../components/functions/animation';
import {
  SiReact, SiMongodb, SiNodedotjs, SiOpenai, SiPython,
} from 'react-icons/si';
import {
  FaBrain, FaServer, FaCode, FaLayerGroup, FaCheckCircle, FaChartBar,
} from 'react-icons/fa';
import { TbApi, TbArrowRight } from 'react-icons/tb';
import './Services.css';

const SERVICES = [
  {
    num: '01',
    icon: <SiReact />,
    title: 'Full-Stack Web Development',
    description:
      'End-to-end web applications and modular-monolith platforms—reactive React, Next.js, and SolidJS frontends backed by Node.js/Express and FastAPI services, JWT authentication, real-time Socket.io/WebRTC, and PostgreSQL/MongoDB data layers.',
    tags: ['React.js', 'Next.js', 'SolidJS', 'Node.js', 'FastAPI', 'PostgreSQL', 'MongoDB'],
    accent: '#64ffda',
    metrics: ['100+ users served', '25% faster load time', 'Modular-monolith builds'],
  },
  {
    num: '02',
    icon: <FaBrain />,
    title: 'AI / ML Engineering & LLM Systems',
    description:
      'Production AI/ML systems—multi-provider LLM orchestration, RAG (Retrieval-Augmented Generation) pipelines, prompt engineering, and OpenCV computer vision. Multi-stage AI document pipelines, real-time gesture detection, and agent workflows with caching for throughput.',
    tags: ['LLM Orchestration', 'RAG', 'Prompt Engineering', 'Python', 'FastAPI', 'OpenCV', 'OpenAI API'],
    accent: '#bd93f9',
    metrics: ['Multi-stage LLM/RAG pipelines', 'Real-time CV detection', 'Agent workflow automation'],
  },
  {
    num: '03',
    icon: <FaServer />,
    title: 'DevOps & Self-Hosted Infrastructure',
    description:
      'CI/CD pipelines, Dockerised microservices, and self-hosted infrastructure—GitHub Actions delivery, QEMU/KVM virtualization, and cloud/VPS deployment on Netlify, Render, and Linux for zero-downtime, production-grade reliability.',
    tags: ['Docker', 'GitHub Actions', 'QEMU/KVM', 'CI/CD', 'Linux VPS', 'Netlify', 'Render'],
    accent: '#ffd166',
    metrics: ['Dockerised microservices', 'Zero-downtime deploys', 'QEMU/KVM self-hosting'],
  },
  {
    num: '04',
    icon: <TbApi />,
    title: 'API Design & Microservices',
    description:
      'Clean RESTful APIs and FastAPI microservices across multi-language pipelines—third-party integrations (payments, auth, LLM webhooks) and real-time transport (Socket.io, WebRTC), with full error handling and documentation.',
    tags: ['REST API', 'FastAPI', 'Microservices', 'WebRTC', 'Socket.io', 'Webhooks', 'JWT Auth'],
    accent: '#ff79c6',
    metrics: ['Multi-language microservices', 'Webhook ingestion systems', 'Real-time transport'],
  },
  {
    num: '05',
    icon: <FaChartBar />,
    title: 'Data Analysis & Dashboards',
    description:
      'Transforming raw data into actionable insights with Python and SQL—automated reporting pipelines, interactive dashboards, and data-driven recommendations, feeding cleaned datasets into ML models where useful.',
    tags: ['Python', 'Pandas', 'SQL', 'PostgreSQL', 'NumPy', 'Power BI', 'Machine Learning'],
    accent: '#50fa7b',
    metrics: ['Automated reports', 'Interactive dashboards', 'SQL query optimization'],
  },
];

const PROCESS = [
  { step: '01', label: 'Discover', desc: 'Understand goals, scope, and requirements' },
  { step: '02', label: 'Plan', desc: 'Architecture, tech stack decisions & timelines' },
  { step: '03', label: 'Build', desc: 'Iterative development with clean, tested code' },
  { step: '04', label: 'Deploy', desc: 'CI/CD, cloud release & post-launch monitoring' },
];

const Services = () => {
  const [activeCard, setActiveCard] = useState(null);

  return (
    <Element name="services">
      <section id="services">
        <div className="container">
          {/* Heading */}
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.2 }}
          >
            <p className="section-label">What I Do</p>
            <h2 className="heading">My <span>Services</span></h2>
            <p className="svc-subtitle">
              From idea to deployment—I cover the full spectrum of modern software development.
            </p>
          </motion.div>

          {/* Cards */}
          <motion.div
            className="services-grid"
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
          >
            {SERVICES.map((svc, idx) => (
              <motion.div
                className={`service-card${activeCard === idx ? ' active' : ''}`}
                key={idx}
                custom={idx}
                variants={cardVariant}
                whileHover={{ y: -8, scale: 1.01 }}
                transition={{ type: 'spring', stiffness: 240, damping: 20 }}
                style={{ '--svc-accent': svc.accent }}
                onClick={() => setActiveCard(activeCard === idx ? null : idx)}
                role="button"
                tabIndex={0}
                aria-pressed={activeCard === idx}
                aria-label={`${svc.title} — toggle details`}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), setActiveCard(activeCard === idx ? null : idx))}
              >
                <span className="svc-num" aria-hidden="true">{svc.num}</span>
                <div className="svc-icon-wrap" aria-hidden="true">
                  {svc.icon}
                </div>
                <h3 className="svc-title">{svc.title}</h3>
                <p className="svc-desc">{svc.description}</p>

                {/* Metrics row */}
                <div className="svc-metrics">
                  {svc.metrics.map((m, k) => (
                    <span key={k} className="svc-metric">{m}</span>
                  ))}
                </div>

                {/* Tags */}
                <ul className="svc-tags">
                  {svc.tags.map((tag, t) => (
                    <li key={t} className="svc-tag">
                      <FaCheckCircle className="svc-tag-icon" aria-hidden="true" />
                      {tag}
                    </li>
                  ))}
                </ul>

                <div className="svc-arrow" aria-hidden="true"><TbArrowRight /></div>
              </motion.div>
            ))}
          </motion.div>

          {/* Process strip */}
          <motion.div
            className="svc-process"
            variants={fadeInUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
          >
            <p className="svc-process-label">How I Work</p>
            <div className="svc-process-steps">
              {PROCESS.map((p, i) => (
                <motion.div
                  key={i}
                  className="svc-process-step"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.12, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  whileHover={{ y: -6, scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <div className="svc-step-num">{p.step}</div>
                  <div className="svc-step-label">{p.label}</div>
                  <p className="svc-step-desc">{p.desc}</p>
                  {i < PROCESS.length - 1 && <div className="svc-step-connector" />}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </Element>
  );
};

export default Services;
